import styled, { css } from 'styled-components'
import { MarginProps, TextAlignProps, textAlign } from 'styled-system'
import { isFunction } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { HeightProps, WidthProps } from 'types/styled'

import Icon from '../Icon'
import React from 'react'
import { Z_INDEX } from 'utils/constants'

export type OptionType = {
  value: string
  label: string
  icon?: string[] | string | React.ReactNode
  isDisabled?: boolean
}

type ListStyleType = MarginProps &
  TextAlignProps & {
    height?: HeightProps
    width?: WidthProps
    align?: 'left' | 'right'
    zIndex?: number
  }

type ListType = ListStyleType & {
  onSelect?: (value: string) => void
  options: OptionType[]
  selected?: string
  zIndex?: number
}

const MenuList = ({
  onSelect,
  selected,
  options,
  height = 'auto',
  width = 'auto',
  align = 'left',
  ...props
}: ListType) => {
  const { t } = useTranslation()

  const handleSelect = (value: string) => {
    if (onSelect && isFunction(onSelect)) {
      onSelect(value)
    }
  }

  const styleProps = { height, width, align, ...props }

  return (
    <Container {...styleProps}>
      {options.map(({ label, value, icon, isDisabled = false }, index) => (
        <Option
          {...styleProps}
          key={index}
          isSelected={value === selected}
          onClick={() => {
            !isDisabled && handleSelect(value)
          }}
          isDisabled={isDisabled}
        >
          {icon && <StyledIcon icon={icon} />}
          {t(label)}
        </Option>
      ))}
    </Container>
  )
}

const Container = styled.div<ListStyleType>`
  box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
  background-color: ${(p) => p.theme.colors.white};
  border-radius: 4px;
  height: ${(p) => (p.height === 'auto' ? 'auto' : `${p.height}px`)};
  max-height: ${(p) => (p.height === 'auto' ? '217' : p.height)}px;
  overflow-y: auto;
  width: ${(p) => (p.width === 'auto' ? 'calc(100% - 16px)' : `${p.width}px`)};
  z-index: ${(p) => (p.zIndex ? p.zIndex : Z_INDEX.menuListPopOver)};
  ${(p) =>
    p.align &&
    (p.align === 'left'
      ? css`
          position: absolute;
          left: 0;
        `
      : css`
          position: absolute;
          right: 0;
        `)}
`

const Option = styled.div<TextAlignProps & { isSelected: boolean; isDisabled: boolean }>`
  ${(p) => p.theme.font({ color: p.isSelected ? 'white' : 'gray7' })};
  background: ${(p) => (p.isSelected ? p.theme.colors.blue1 : p.theme.colors.white)};
  border-bottom: 1px solid ${(p) => p.theme.colors.gray3};
  cursor: ${(p) => (p.isDisabled ? 'not-allowed' : 'pointer')};
  padding: 16px 14px;
  ${textAlign}

  &:hover {
    background: ${(p) => (p.isSelected ? p.theme.colors.blue1 : p.theme.colors.gray2)};
  }
`

const StyledIcon = styled((p) => <Icon {...p} />)`
  margin-right: 20px;
`

export default MenuList
